/*
  Vehicle fitment.

  Theme.Fitment keeps the shopper's saved vehicle, loads the vehicle database
  on demand, reads wheel specs from product data and works out whether a wheel
  should bolt on. The custom elements below render that for the storefront:

    vehicle-picker       year / make / model selects
    vehicle-menu         header button and dialog
    fitment-badge        verdict on a product card
    product-fitment      verdict and vehicle specs on the product page
    vehicle-results-bar  saved vehicle and bolt pattern filter on listings
    vehicle-finder       homepage search with coverage count and saved vehicle

  The vehicle database is split so pages only download what they use:
    vehicle-index.json        every make / model / year range (vehicle picker)
    vehicle-make-<make>.json  full rows and trims for one make
    vehicle-all.json          every row (the "vehicles this wheel fits" chart)

  Vehicle database rows:
    { make, model, from, to, pcd: [bolt patterns], bore, lug, torque, oemSizes,
      oem: { fw, fo, rw, ro, dmin, dmax, d },
      fit: { [boltPattern]: [boltOnFront, boltOnRear, modsFront, modsRear] },
      setups: [...], est: true when the envelope is estimated }
  Each envelope is [minDiameter, maxDiameter, minWidth, maxWidth, maxOuter, maxInner]
  where outer = width * 12.7 - offset (mm outboard of the hub face) and
  inner = width * 12.7 + offset (mm inboard of the hub face).
*/
(function () {
  const Theme = window.Theme;
  if (!Theme) return;

  const config = (Theme.settings && Theme.settings.fitment) || {};
  const strings = (Theme.strings && Theme.strings.fitment) || {};
  const STORAGE_KEY = 'theme:vehicle';
  const TRIM_SEPARATOR = '|';
  const TRIM_MARK = '·';
  const HALF_INCH_MM = 12.7;
  const OUTER_TOLERANCE_MM = 5;
  const INNER_TOLERANCE_MM = 3;
  const RANK = { 'bolt-on': 3, mods: 2, unknown: 1, no: 0 };
  const NARROW_OK_IN = 1.5;
  const NARROW_LIMIT_IN = 2.5;
  const DIAMETER_GRACE_IN = 1;
  const BORE_TOLERANCE_MM = 0.15;

  let indexPromise = null;
  let allPromise = null;
  const makePromises = new Map();
  let arrivalHandled = false;
  let leaving = false;
  let savedVehicle;
  let storageFailed = false;

  /* Parsing */

  /* Bolt circles that sellers still write in inches. */
  const INCH_PATTERNS = {
    '4x4.5': '4x114.3',
    '5x4.5': '5x114.3',
    '5x4.75': '5x120.65',
    '5x5': '5x127',
    '5x5.5': '5x139.7',
    '6x5.5': '6x139.7',
    '8x6.5': '8x165.1',
  };
  const NUMBER = '\\d{1,3}(?:\\.\\d+)?';
  const SHORT_NUMBER = '\\d{1,2}(?:\\.\\d{1,2})?';
  const INCH_MARK = '(?:\\s*(?:"|in\\b\\.?|inch(?:es)?\\b))?(?:\\s*J{1,2})?';
  const SIZE = `(?:^|[^\\d.])(${SHORT_NUMBER})${INCH_MARK}\\s*x\\s*(${SHORT_NUMBER})(?![\\d.]|\\s*x|\\s*R\\s*\\d\\d)`;
  const SIGNED_OFFSET = `([+-]\\d{1,3}(?:\\.\\d+)?)(?:\\s*mm)?(?=$|[\\s),/])`;
  const ET_OFFSET = `(?:^|[^a-z])ET\\s*([+-]?\\d{1,3}(?:\\.\\d+)?)(?![\\d.])`;

  /* One spelling for the characters sellers use for "by", minus and inches. */
  function normalize(text) {
    return String(text === null || text === undefined ? '' : text)
      .replace(/[×✕*]/g, 'x')
      .replace(/[−–—]/g, '-')
      .replace(/[″“”]|''/g, '"');
  }

  function boltKey(lugs, circle, inchAllowed) {
    const value = parseFloat(circle);
    if (value >= 95 && value <= 230) return `${lugs}x${value === 120.7 ? 120.65 : value}`;
    return (inchAllowed && INCH_PATTERNS[`${lugs}x${value}`]) || null;
  }

  /*
    Bolt patterns in a piece of text, as "5x114.3". Free text (titles) must use
    "x"; a dedicated bolt pattern value may also use "5-114.3", "5/114.3" or
    "5 on 4.5". Inch circles are converted, so 5x4.5 reads as 5x114.3.
  */
  function parseBoltPatterns(text, dedicated) {
    const found = [];
    const source = normalize(text);
    if (!source) return found;
    const separator = dedicated ? '(?:x|on|-|\\/)' : 'x';
    const pattern = new RegExp(
      `(?:^|[^\\d.])([3-8])\\s*${separator}\\s*(${NUMBER})((?:\\s*\\/\\s*${NUMBER}(?![\\d.]|\\s*x))*)(?![\\d.])`,
      'gi'
    );
    let match;
    while ((match = pattern.exec(source))) {
      const inches = parseFloat(match[2]) < 20;
      const circles = [match[2]].concat(match[3] ? match[3].split('/').slice(1) : []);
      circles.forEach((circle, index) => {
        const key = boltKey(match[1], circle, index === 0 || inches);
        if (key && !found.includes(key)) found.push(key);
      });
    }
    return found;
  }

  function asSize(diameter, width) {
    const valid = Number.isInteger(diameter) && diameter >= 12 && diameter <= 30 && width >= 3.5 && width <= 14 && width < diameter;
    return valid ? { d: diameter, w: width } : null;
  }

  /* Wheel sizes in a piece of text: "18x8.5", "8.5Jx18", 18" x 8.5". */
  function parseSizes(text) {
    const found = [];
    const source = normalize(text);
    if (!source) return found;
    const pattern = new RegExp(SIZE, 'gi');
    let match;
    while ((match = pattern.exec(source))) {
      const first = parseFloat(match[1]);
      const second = parseFloat(match[2]);
      const size = asSize(first, second) || asSize(second, first);
      if (size) found.push(size);
    }
    return found;
  }

  /*
    Offsets in free text: "ET35", or a signed number such as "+35" or "-12".
    With afterSize only a signed number written straight after a size counts,
    so "+2 bundle" in a product title is not read as an offset.
  */
  function parseOffsets(text, afterSize) {
    const found = [];
    const source = normalize(text);
    if (!source) return found;
    const signed = afterSize
      ? `x\\s*${SHORT_NUMBER}${INCH_MARK}"?[\\s,/(]*${SIGNED_OFFSET}`
      : `(?:^|[\\s(,/])${SIGNED_OFFSET}`;
    const pattern = new RegExp(`${ET_OFFSET}|${signed}`, 'gi');
    let match;
    while ((match = pattern.exec(source))) {
      const value = parseFloat(match[1] !== undefined ? match[1] : match[2]);
      if (value >= -100 && value <= 100) found.push(value);
    }
    return found;
  }

  /* Offsets in a dedicated offset value: "35", "+35", "ET 35", "35mm", "+35 / +40". */
  function parseOffsetOption(text) {
    const found = [];
    const source = normalize(text).replace(/ET/gi, ' ');
    const pattern = /(?:^|[^\d.])([+-]?\d{1,3}(?:\.\d+)?)(?![\d.]|\s*x)/g;
    let match;
    while ((match = pattern.exec(source))) {
      const value = parseFloat(match[1]);
      if (value >= -100 && value <= 100) found.push(value);
    }
    return found;
  }

  function optionValue(product, variant, optionName) {
    if (!optionName || !product.options) return '';
    const wanted = String(optionName).trim().toLowerCase();
    const index = product.options.findIndex((name) => String(name).trim().toLowerCase() === wanted);
    return index > -1 && variant.options ? String(variant.options[index] || '').trim() : '';
  }

  function distinctSizes(sizes) {
    return sizes.filter((size, index) => sizes.findIndex((other) => other.d === size.d && other.w === size.w) === index);
  }

  /* The first text that gives a result. A product title that lists several values is not specific to one variant. */
  function firstParsed(product, variant, parse, texts) {
    for (let index = 0; index < texts.length; index += 1) {
      const values = parse(texts[index]);
      if (values.length) return values;
    }
    const shared = parse(product.title, true);
    return shared.length > 1 && (product.variants || []).length > 1 ? [] : shared;
  }

  /*
    Reads { front, rear, pcds } for one variant. rear is null for a square set.
    A value in the option named by the theme settings always wins; titles are
    only read when that option is missing. Anything that can be read two ways
    is left out, so the verdict becomes "not confirmed" instead of a guess.
  */
  function wheelFromVariant(product, variant) {
    const sizeText = optionValue(product, variant, config.optionSize);
    const offsetText = optionValue(product, variant, config.optionOffset);
    const boltText = optionValue(product, variant, config.optionBoltPattern);
    const texts = [sizeText, variant.title];

    const sizes = distinctSizes(
      sizeText ? parseSizes(sizeText) : firstParsed(product, variant, (text) => distinctSizes(parseSizes(text)), texts)
    );
    const offsets = offsetText
      ? parseOffsetOption(offsetText)
      : firstParsed(product, variant, (text, shared) => parseOffsets(text, shared), texts);
    const pcds = boltText ? parseBoltPatterns(boltText, true) : firstParsed(product, variant, (text) => parseBoltPatterns(text), texts);

    const wheel = { pcds, front: null, rear: null };
    const bore = parseBore(`${(variant.options || []).join(' ')} ${variant.title || ''}`);
    if (bore) wheel.bore = bore;
    if (!sizes.length || sizes.length > 2) return wheel;

    let ets = [null, null];
    if (offsets.length === 1) ets = [offsets[0], offsets[0]];
    else if (offsets.length === 2 && (sizes.length === 2 || offsets[0] === offsets[1])) ets = offsets.slice();

    /* the narrower wheel of a staggered pair goes on the front */
    if (sizes[1] && (sizes[0].w > sizes[1].w || (sizes[0].w === sizes[1].w && sizes[0].d > sizes[1].d))) {
      sizes.reverse();
      ets.reverse();
    }
    wheel.front = { d: sizes[0].d, w: sizes[0].w, et: ets[0] };
    if (sizes[1]) wheel.rear = { d: sizes[1].d, w: sizes[1].w, et: ets[1] };
    return wheel;
  }

  /* Center bore in mm when a variant states one, for example "CB72.6" or "CB: 66.56". */
  function parseBore(text) {
    const match = /\bCB\s*:?\s*(\d{2,3}(?:\.\d+)?)/i.exec(text || '');
    const value = match ? parseFloat(match[1]) : 0;
    return value >= 50 && value <= 135 ? value : null;
  }

  /* Verdicts */

  /*
    One axle against the vehicle's ranges. A wheel does not have to match the
    factory size to fit: what matters is that its diameter, width and offset
    stay inside what the vehicle can take.

    - Position: the outer edge (width and offset) and the inner edge must stay
      inside the direct fit range, or the wider range that needs modifications.
    - Width: up to NARROW_OK_IN narrower than the factory wheel is still a
      direct fit; narrower than that needs checking, and far narrower is out.
    - Diameter: anything in the known range fits. One size beyond it is
      possible but needs checking (brake clearance below, tire profile above).
  */
  function checkAxle(envelopes, spec, axle, factory) {
    const boltOn = axle === 'rear' ? envelopes[1] : envelopes[0];
    const withMods = axle === 'rear' ? envelopes[3] : envelopes[2];
    const outer = spec.w * HALF_INCH_MM - spec.et;
    const inner = spec.w * HALF_INCH_MM + spec.et;
    const factoryWidth = (factory && (axle === 'rear' ? factory.rw : factory.fw)) || boltOn[2];
    const issues = [];
    const flag = (level, reason) => issues.push({ level, reason });

    if (spec.d < withMods[0] - DIAMETER_GRACE_IN) flag('no', 'tooSmall');
    else if (spec.d < withMods[0]) flag('mods', 'smallerDiameter');
    else if (spec.d > withMods[1] + DIAMETER_GRACE_IN) flag('no', 'tooBig');
    else if (spec.d > withMods[1]) flag('mods', 'largerDiameter');
    else if (spec.d > boltOn[1]) flag('mods', 'withMods');

    if (spec.w > withMods[3]) flag('no', 'tooWide');
    else if (spec.w > boltOn[3]) flag('mods', 'withMods');
    if (spec.w < factoryWidth - NARROW_LIMIT_IN) flag('no', 'tooNarrow');
    else if (spec.w < factoryWidth - NARROW_OK_IN) flag('mods', 'narrow');

    const clears = (envelope) => outer <= envelope[4] + OUTER_TOLERANCE_MM && inner <= envelope[5] + INNER_TOLERANCE_MM;
    if (!clears(withMods)) flag('no', outer > withMods[4] + OUTER_TOLERANCE_MM ? 'pokes' : 'tooDeep');
    else if (!clears(boltOn)) flag('mods', 'withMods');

    issues.sort((first, second) => RANK[first.level] - RANK[second.level]);
    const worst = issues[0];
    return { level: worst ? worst.level : 'bolt-on', reason: worst ? worst.reason : 'boltOn', axle };
  }

  /* Both axles against the envelopes of one bolt pattern. */
  function checkPattern(row, wheel, boltPattern) {
    const front = checkAxle(row.fit[boltPattern], wheel.front, 'front', row.oem);
    const rear = checkAxle(row.fit[boltPattern], wheel.rear || wheel.front, 'rear', row.oem);
    const worst = RANK[rear.level] < RANK[front.level] ? rear : front;
    return {
      level: worst.level,
      reason: worst.reason,
      axle: front.level !== rear.level || front.reason !== rear.reason ? worst.axle : null,
      pcd: boltPattern,
    };
  }

  /*
    Compares one wheel with one vehicle row. options.boltPattern skips the bolt
    pattern comparison and checks size and offset for that pattern only; the
    size guide uses it because the shopper types a size, not a product.
  */
  function evaluate(row, wheel, options) {
    const result = compare(row, wheel || { pcds: [], front: null, rear: null }, (options && options.boltPattern) || null);
    if (row && row.unverified) result.unverified = true;
    if (wheel && wheel.bore && row && row.bore && wheel.bore > row.bore + BORE_TOLERANCE_MM && result.level !== 'no') {
      result.rings = true;
    }
    return result;
  }

  function compare(row, wheel, assumedPattern) {
    if (!row) return { level: 'unknown', reason: 'noData' };

    const rowPatterns = row.pcd || [];
    const pcds = assumedPattern ? [assumedPattern] : wheel.pcds || [];
    const shared = pcds.filter((pcd) => rowPatterns.includes(pcd));
    if (pcds.length && !shared.length) return { level: 'no', reason: 'wrongBoltPattern' };
    /* a wheel whose center bore is smaller than the hub cannot be mounted */
    if (wheel.bore && row.bore && wheel.bore < row.bore - BORE_TOLERANCE_MM) return { level: 'no', reason: 'boreTooSmall' };

    const sized = Boolean(wheel.front) && wheel.front.et !== null && (!wheel.rear || wheel.rear.et !== null);
    /* without a bolt pattern nothing may be called a fit, whatever the size says */
    if (!shared.length) return { level: 'unknown', reason: sized ? 'noBoltPattern' : 'noSpecs' };
    if (!sized || !row.fit) return { level: 'unknown', reason: 'boltPatternOnly', pcd: shared[0] };

    const checked = shared.filter((pcd) => row.fit[pcd]).map((pcd) => checkPattern(row, wheel, pcd));
    if (!checked.length) return { level: 'unknown', reason: 'boltPatternOnly', pcd: shared[0] };
    const byRank = checked.slice().sort((first, second) => RANK[first.level] - RANK[second.level]);
    const worst = byRank[0];
    const best = byRank[byRank.length - 1];
    const fits = (result) => result.level === 'bolt-on' || result.level === 'mods';
    /* a pattern the vehicle was built with that this wheel does not have */
    const uncovered = !assumedPattern && rowPatterns.some((pcd) => !pcds.includes(pcd));

    if (!fits(best)) return best;
    if (fits(worst) && !uncovered) return worst;
    /*
      The vehicle was built with several bolt patterns and the wheel fits only
      some of them, so the answer depends on which one this car has.
    */
    return { level: 'unknown', reason: 'patternVaries', pcd: best.pcd, patterns: rowPatterns.slice(), sizeLevel: best.level };
  }

  /* Best verdict across a product's variants, preferring ones in stock. */
  function evaluateProduct(row, product) {
    let best = null;
    (product.variants || []).forEach((variant) => {
      const result = evaluate(row, wheelFromVariant(product, variant));
      const score = RANK[result.level] * 2 + (variant.available === false ? 0 : 1);
      if (!best || score > best.score) best = { result, score };
    });
    return best ? best.result : { level: 'unknown', reason: 'noSpecs' };
  }

  /* Offsets that keep a wheel of this width inside an envelope, or null. */
  function offsetRange(envelope, width) {
    const low = Math.ceil(width * HALF_INCH_MM - envelope[4] - OUTER_TOLERANCE_MM);
    const high = Math.floor(envelope[5] + INNER_TOLERANCE_MM - width * HALF_INCH_MM);
    return low <= high ? [low, high] : null;
  }

  function fetchJSON(url) {
    return fetch(url).then((response) => {
      if (!response.ok) throw new Error(`Vehicle data request failed: ${response.status}`);
      return response.json();
    });
  }

  function validVehicle(vehicle) {
    const valid =
      vehicle &&
      typeof vehicle === 'object' &&
      Number(vehicle.year) > 0 &&
      typeof vehicle.make === 'string' &&
      typeof vehicle.model === 'string' &&
      vehicle.make &&
      vehicle.model &&
      (vehicle.trim === undefined || typeof vehicle.trim === 'string');
    return valid ? vehicle : null;
  }

  const Fitment = {
    checkAxle,
    trims: {},
    index: null,
    parseBoltPatterns,
    parseSizes,
    parseOffsets,
    wheelFromVariant,
    evaluate,
    evaluateProduct,
    offsetRange,

    /* Light rows { make, model, from, to } for every vehicle: enough for the picker. */
    loadIndex() {
      if (!indexPromise) {
        indexPromise = fetchJSON(config.indexUrl)
          .then((data) => {
            Fitment.index = data;
            const rows = [];
            Object.keys(data.makes).forEach((make) => {
              data.makes[make].models.forEach((entry) => rows.push({ make, model: entry[0], from: entry[1], to: entry[2] }));
            });
            return rows;
          })
          .catch((error) => {
            indexPromise = null;
            throw error;
          });
      }
      return indexPromise;
    },

    /* Full rows for one make. Also registers that make's trim names. */
    loadMake(make) {
      if (!makePromises.has(make)) {
        const request = Fitment.loadIndex()
          .then(() => {
            const makes = Fitment.index.makes;
            const entry = Object.prototype.hasOwnProperty.call(makes, make) ? makes[make] : null;
            if (!entry || !entry.file) return { vehicles: [], trims: {} };
            const url = config.indexUrl.replace(/vehicle-index\.json.*$/, `${entry.file}?v=${Fitment.index.stamp || Fitment.index.combinations}`);
            return fetchJSON(url);
          })
          .then((data) => {
            Object.assign(Fitment.trims, data.trims || {});
            return data.vehicles || [];
          })
          .catch((error) => {
            makePromises.delete(make);
            throw error;
          });
        makePromises.set(make, request);
      }
      return makePromises.get(make);
    },

    /* The database row for a saved vehicle, or null. */
    async getRow(vehicle) {
      if (!vehicle) return null;
      return Fitment.findRow(await Fitment.loadMake(vehicle.make), vehicle);
    },

    /* Every row. Large: only for features that compare a wheel with all vehicles. */
    load() {
      if (!allPromise) {
        allPromise = fetchJSON(config.allUrl)
          .then((data) => data.vehicles || [])
          .catch((error) => {
            allPromise = null;
            throw error;
          });
      }
      return allPromise;
    },

    /* The saved vehicle. Kept in memory as well, so it works for this page when storage is blocked. */
    getVehicle() {
      if (savedVehicle === undefined) savedVehicle = validVehicle(Theme.storageGet(STORAGE_KEY));
      return savedVehicle;
    },

    setVehicle(vehicle) {
      savedVehicle = validVehicle(vehicle);
      Theme.storageSet(STORAGE_KEY, savedVehicle);
      storageFailed = Boolean(savedVehicle) && !validVehicle(Theme.storageGet(STORAGE_KEY));
      document.documentElement.classList.toggle('has-vehicle', Boolean(savedVehicle));
      Theme.emit('vehicle:changed', { vehicle: savedVehicle });
    },

    label(vehicle) {
      if (!vehicle) return '';
      return `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? ` ${vehicle.trim}` : ''}`;
    },

    /* Trim names offered for a model in a given year, for example "Si" or "Type R". */
    trimsFor(make, model, year) {
      const key = `${make}|${model}`;
      const trims = Object.prototype.hasOwnProperty.call(this.trims, key) ? this.trims[key] : [];
      return trims.filter((trim) => year >= trim[1] && year <= trim[2]).map((trim) => trim[0]);
    },

    findRow(rows, vehicle) {
      if (!vehicle) return null;
      const year = Number(vehicle.year);
      return (
        rows.find((row) => row.make === vehicle.make && row.model === vehicle.model && year >= row.from && year <= row.to) ||
        null
      );
    },

    years(rows) {
      const years = new Set();
      rows.forEach((row) => {
        for (let year = row.from; year <= row.to; year += 1) years.add(year);
      });
      return Array.from(years).sort((a, b) => b - a);
    },

    makes(rows, year) {
      return Array.from(new Set(rows.filter((row) => year >= row.from && year <= row.to).map((row) => row.make))).sort();
    },

    models(rows, year, make) {
      return Array.from(
        new Set(rows.filter((row) => row.make === make && year >= row.from && year <= row.to).map((row) => row.model))
      ).sort();
    },

    verdictLabel(level, result) {
      if (level === 'unknown' && result && result.reason === 'boltPatternOnly') return strings.patternMatch;
      return { 'bolt-on': strings.boltOn, mods: strings.withMods, no: strings.noFit, unknown: strings.unknown }[level];
    },

    /* Higher fits better. Used to pick the best variant and to order listings. */
    rank(level) {
      return RANK[level] || 0;
    },

    verdictDetail(result) {
      let detail = strings[`${result.reason}Detail`] || strings[result.reason] || '';
      if (result.reason === 'patternVaries') {
        detail = detail.replace('[patterns]', (result.patterns || []).join(', ')).replace('[pattern]', result.pcd || '');
      }
      if (result.axle && detail) detail = `${result.axle === 'rear' ? strings.rear : strings.front}: ${detail}`;
      if (result.rings && strings.rings) detail = `${detail} ${strings.rings}`.trim();
      if (result.unverified && strings.unverified) detail = `${detail} ${strings.unverified}`.trim();
      return detail;
    },

    badgeClass(level) {
      return { 'bolt-on': 'badge--fit', mods: 'badge--warning', no: 'badge--error', unknown: '' }[level];
    },
  };

  Theme.Fitment = Fitment;

  /* Picks up a vehicle changed in another tab, or before a page was restored from the back/forward cache. */
  function syncVehicle() {
    if (storageFailed) return;
    const stored = validVehicle(Theme.storageGet(STORAGE_KEY));
    if (Fitment.label(stored) === Fitment.label(Fitment.getVehicle())) return;
    savedVehicle = stored;
    Theme.emit('vehicle:changed', { vehicle: stored, synced: true });
  }

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY || event.key === null) syncVehicle();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncVehicle();
  });

  /* Small DOM helpers */

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  /* Writes text only when it differs, so live regions announce a change once. */
  function setText(node, text) {
    const value = Theme.decode ? Theme.decode(text || '') : text || '';
    if (node && node.textContent !== value) node.textContent = value;
  }

  function readJSON(root) {
    const script = root.querySelector('script[type="application/json"]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  /* One observer for every card, picker and section that waits to be seen. */
  let visibilityObserver = null;
  const visibilityCallbacks = new WeakMap();

  function whenVisible(target, callback) {
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }
    if (!visibilityObserver) {
      visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const run = visibilityCallbacks.get(entry.target);
            visibilityObserver.unobserve(entry.target);
            visibilityCallbacks.delete(entry.target);
            if (run) run();
          });
        },
        { rootMargin: '200px' }
      );
    }
    /* observing again restarts the check for an element that was moved */
    visibilityObserver.unobserve(target);
    visibilityCallbacks.set(target, callback);
    visibilityObserver.observe(target);
  }

  function focusPicker(container) {
    const picker = container.querySelector('vehicle-picker');
    if (picker && typeof picker.focusWhenReady === 'function') picker.focusWhenReady();
  }

  /* vehicle-picker */

  class VehiclePicker extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.year = this.querySelector('[data-vehicle-year]');
      this.make = this.querySelector('[data-vehicle-make]');
      this.model = this.querySelector('[data-vehicle-model]');
      this.submit = this.querySelector('[data-vehicle-submit]');
      this.status = this.querySelector('[data-vehicle-status]');
      if (!this.form || !this.year || !this.make || !this.model || !this.submit) return;

      if (!this.bound) {
        this.bound = true;
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.year.addEventListener('change', () => this.fillMakes());
        this.make.addEventListener('change', () => this.onMakeChange());
        this.model.addEventListener('change', () => this.updateSubmit());
      }

      if (this.stopListening) this.stopListening();
      this.stopListening = Theme.on('vehicle:changed', (event) => this.select(event.detail.vehicle));
      if (!this.rows) whenVisible(this, () => this.start());
    }

    disconnectedCallback() {
      if (this.stopListening) this.stopListening();
      this.stopListening = null;
    }

    /* Loads the vehicle list once. Calling it again after a failed request retries. */
    start() {
      if (!this.ready) this.ready = this.load();
      return this.ready;
    }

    async load() {
      try {
        this.rows = await Fitment.loadIndex();
      } catch (error) {
        this.ready = null;
        setText(this.status, strings.loadError);
        return false;
      }
      setText(this.status, '');
      this.fill(this.year, Fitment.years(this.rows));
      this.year.disabled = false;
      this.select(Fitment.getVehicle());
      return true;
    }

    async focusWhenReady() {
      await this.start();
      if (!this.year.disabled) this.year.focus();
    }

    hasOption(select, value) {
      return Array.from(select.options).some((option) => option.value === value);
    }

    fill(select, values) {
      const placeholder = select.options[0];
      select.replaceChildren(placeholder);
      values.forEach((value) => select.append(new Option(value, value)));
      select.value = '';
    }

    fillMakes(selected) {
      const year = Number(this.year.value);
      this.fill(this.make, year ? Fitment.makes(this.rows, year) : []);
      this.make.disabled = !year;
      if (selected && this.hasOption(this.make, selected)) this.make.value = selected;
      this.fillModels();
    }

    fillModels(selected) {
      const year = Number(this.year.value);
      const make = this.make.value;
      this.fill(this.model, year && make ? Fitment.models(this.rows, year, make) : []);
      this.model.disabled = !make;
      if (selected && this.hasOption(this.model, selected)) this.model.value = selected;
      this.updateSubmit();
    }

    /* The model list stays locked until its trims are in, so it never changes while it is open. */
    async onMakeChange() {
      this.fillModels();
      if (!this.make.value) return;
      const make = this.make.value;
      this.model.disabled = true;
      await this.addTrims();
      if (this.make.value === make) this.model.disabled = false;
    }

    /*
      Once the make's data has loaded, lists each model's trims for the chosen
      year under it. Options are added, never rebuilt, so a choice the shopper
      made while the data was loading is kept.
    */
    async addTrims(selected) {
      const year = Number(this.year.value);
      const make = this.make.value;
      if (!year || !make) return;
      try {
        await Fitment.loadMake(make);
      } catch (error) {
        return;
      }
      if (this.make.value !== make || Number(this.year.value) !== year) return;

      Array.from(this.model.options).forEach((option) => {
        if (!option.value || option.value.includes(TRIM_SEPARATOR) || option.hasAttribute('data-trims')) return;
        option.setAttribute('data-trims', '');
        const trims = Fitment.trimsFor(make, option.value, year);
        if (trims.length < 2) return;
        let last = option;
        trims.forEach((trim) => {
          const item = new Option(`${option.value} ${TRIM_MARK} ${trim}`, option.value + TRIM_SEPARATOR + trim);
          last.after(item);
          last = item;
        });
      });

      const untouched = selected && this.model.value === selected.split(TRIM_SEPARATOR)[0];
      if (untouched && this.hasOption(this.model, selected)) this.model.value = selected;
      this.updateSubmit();
    }

    updateSubmit() {
      this.submit.disabled = !(this.year.value && this.make.value && this.model.value);
    }

    /* Shows a vehicle in the selects. One that is no longer in the data leaves the picker empty. */
    select(vehicle) {
      if (!this.rows) return;
      const year = vehicle ? String(vehicle.year) : '';
      this.year.value = this.hasOption(this.year, year) ? year : '';
      this.fillMakes(vehicle ? vehicle.make : '');
      if (!vehicle || !this.make.value) return;
      this.fillModels(vehicle.model);
      if (this.model.value) this.addTrims(vehicle.trim ? vehicle.model + TRIM_SEPARATOR + vehicle.trim : vehicle.model);
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.submit.disabled) return;
      const [model, trim] = this.model.value.split(TRIM_SEPARATOR);
      const vehicle = { year: Number(this.year.value), make: this.make.value, model };
      if (trim) vehicle.trim = trim;

      const target = this.hasAttribute('data-redirect') && config.collectionUrl ? new URL(config.collectionUrl, window.location.origin) : null;
      /* already on that listing: its results bar applies the filter without a second page load */
      const alreadyThere = target && target.pathname === window.location.pathname && document.querySelector('vehicle-results-bar');
      leaving = Boolean(target) && !alreadyThere;
      Fitment.setVehicle(vehicle);
      if (leaving) {
        target.searchParams.set('fit', '1');
        window.location.assign(target.toString());
      }
    }
  }

  /* vehicle-menu: header button that opens the picker in a dialog */

  class VehicleMenu extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.toggle = this.querySelector('[data-vehicle-open]');
      this.labelNode = this.querySelector('[data-vehicle-label]');
      this.clearButton = this.querySelector('[data-vehicle-clear]');
      if (!this.dialog || !this.toggle) return;

      if (!this.bound) {
        this.bound = true;
        this.native = typeof this.dialog.showModal === 'function';
        this.toggle.addEventListener('click', () => this.open());
        this.querySelectorAll('[data-vehicle-close]').forEach((button) => button.addEventListener('click', () => this.close()));
        /* a click on the backdrop lands on the dialog itself, outside its box */
        this.dialog.addEventListener('click', (event) => {
          if (event.target !== this.dialog) return;
          const box = this.dialog.getBoundingClientRect();
          const inside =
            event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
          if (!inside) this.close();
        });
        if (!this.native) {
          this.dialog.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') this.close();
          });
        }
      }

      if (this.stopListening) this.stopListening();
      this.stopListening = Theme.on('vehicle:changed', (event) => {
        this.render(event.detail.vehicle);
        if (event.detail.vehicle && !event.detail.synced) this.close();
      });
      this.render(Fitment.getVehicle());
    }

    disconnectedCallback() {
      if (this.stopListening) this.stopListening();
      this.stopListening = null;
    }

    isOpen() {
      return this.dialog.hasAttribute('open');
    }

    open() {
      if (this.isOpen()) return;
      if (this.native) {
        this.dialog.showModal();
      } else {
        /* browsers without <dialog> support: shown as a fixed panel with focus kept inside */
        this.dialog.setAttribute('role', 'dialog');
        this.dialog.setAttribute('aria-modal', 'true');
        this.dialog.setAttribute('open', '');
        Theme.trapFocus(this.dialog);
      }
      const picker = this.dialog.querySelector('vehicle-picker');
      if (picker && typeof picker.start === 'function') picker.start();
    }

    close() {
      if (!this.isOpen()) return;
      if (this.native) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        Theme.releaseFocus();
      }
    }

    render(vehicle) {
      if (this.labelNode) this.labelNode.textContent = vehicle ? Fitment.label(vehicle) : this.labelNode.dataset.emptyLabel;
      this.classList.toggle('vehicle-menu--active', Boolean(vehicle));
      /* cleared from inside the dialog: nothing left to do there */
      if (!vehicle && this.isOpen() && !this.dialog.contains(document.activeElement)) this.close();
    }
  }

  /* vehicle-strip: the saved vehicle in full, under the header on small screens */

  class VehicleStrip extends HTMLElement {
    connectedCallback() {
      this.labelNode = this.querySelector('[data-vehicle-strip-label]');
      const button = this.querySelector('[data-vehicle-strip-open]');
      if (!this.labelNode || !button) return;
      if (!this.bound) {
        this.bound = true;
        button.addEventListener('click', () => {
          const menu = document.querySelector('vehicle-menu');
          if (menu && typeof menu.open === 'function') menu.open();
        });
      }
      if (this.stopListening) this.stopListening();
      this.stopListening = Theme.on('vehicle:changed', (event) => this.render(event.detail.vehicle));
      this.render(Fitment.getVehicle());
    }

    disconnectedCallback() {
      if (this.stopListening) this.stopListening();
      this.stopListening = null;
    }

    render(vehicle) {
      this.labelNode.textContent = vehicle ? Fitment.label(vehicle) : '';
      this.hidden = !vehicle;
    }
  }

  /* fitment-badge: verdict on a product card */

  class FitmentBadge extends HTMLElement {
    connectedCallback() {
      this.product = this.product || readJSON(this);
      if (!this.product) return;
      this.requests = this.requests || 0;
      if (this.stopListening) this.stopListening();
      this.stopListening = Theme.on('vehicle:changed', (event) => this.render(event.detail.vehicle));
      /* The badge itself is hidden until it has a verdict, and a hidden element never reports
         as visible, so watch the card around it. */
      if (Fitment.getVehicle()) whenVisible(this.closest('.product-card') || this.parentElement, () => this.render(Fitment.getVehicle()));
    }

    disconnectedCallback() {
      if (this.stopListening) this.stopListening();
      this.stopListening = null;
    }

    clear() {
      if (this.badge) this.badge.remove();
      this.badge = null;
      this.hidden = true;
    }

    async render(vehicle) {
      const request = (this.requests += 1);
      if (!vehicle) {
        this.clear();
        return;
      }
      let row;
      try {
        row = await Fitment.getRow(vehicle);
      } catch (error) {
        if (request === this.requests) this.clear();
        return;
      }
      /* a newer vehicle was chosen while this one was loading */
      if (request !== this.requests) return;

      const result = Fitment.evaluateProduct(row, this.product);
      this.clear();
      /* a card that carries only some of the variants cannot rule the product out */
      const patternOnly = result.level === 'unknown' && result.reason === 'boltPatternOnly';
      this.dataset.fitRank = String(Fitment.rank(result.level));
      if ((result.level === 'unknown' && !patternOnly) || (result.level === 'no' && this.product.partial)) return;
      this.badge = element('span', `badge ${Fitment.badgeClass(result.level)}`);
      this.badge.append(element('span', 'visually-hidden', `${Fitment.label(vehicle)}: `), Fitment.verdictLabel(result.level, result));
      this.append(this.badge);
      this.hidden = false;
    }
  }

  /* product-fitment: verdict for the selected variant plus vehicle hub specs */

  class ProductFitment extends HTMLElement {
    connectedCallback() {
      this.product = this.product || readJSON(this);
      if (!this.product || !Array.isArray(this.product.variants)) return;
      this.prompt = this.querySelector('[data-fitment-prompt]');
      this.panel = this.querySelector('[data-fitment-result]');
      this.verdict = this.querySelector('[data-fitment-verdict]');
      this.detail = this.querySelector('[data-fitment-detail]');
      this.vehicleName = this.querySelector('[data-fitment-vehicle]');
      this.specs = this.querySelector('[data-fitment-specs]');
      if (!this.prompt || !this.panel || !this.verdict || !this.detail || !this.vehicleName || !this.specs) return;

      if (!this.bound) {
        this.bound = true;
        this.requests = 0;
        this.variantId = Number(this.dataset.variantId) || null;
        this.querySelectorAll('[data-fitment-pick]').forEach((button) =>
          button.addEventListener('click', () => {
            if (this.better) this.selectVariant(this.better);
          })
        );
        this.querySelectorAll('[data-fitment-change]').forEach((button) =>
          button.addEventListener('click', () => {
            this.changing = true;
            this.showPrompt();
            focusPicker(this.prompt);
          })
        );
      }

      (this.stops || []).forEach((stop) => stop());
      this.stops = [
        Theme.on('vehicle:changed', () => {
          this.changing = false;
          this.render();
        }),
        Theme.on('variant:changed', (event) => {
          const detail = event.detail || {};
          if (detail.sectionId && detail.sectionId !== this.dataset.sectionId) return;
          this.variant = detail.variant || null;
          this.variantId = detail.variant ? Number(detail.variant.id) : null;
          /* leave the picker open while the shopper is changing vehicle */
          if (!this.changing) this.render();
        }),
      ];
      this.render();
    }

    disconnectedCallback() {
      (this.stops || []).forEach((stop) => stop());
      this.stops = [];
    }

    showPrompt() {
      this.prompt.hidden = false;
      this.panel.hidden = true;
    }

    /* The selected variant: from the embedded list, else the one the variant picker announced. */
    currentVariant() {
      if (this.variantId === null) return null;
      const matches = (item) => item && Number(item.id) === this.variantId && Array.isArray(item.options);
      return this.product.variants.find(matches) || [this.variant, this.product.selected].find(matches) || null;
    }

    async render() {
      const request = (this.requests += 1);
      const vehicle = Fitment.getVehicle();
      if (!vehicle) {
        this.showPrompt();
        return;
      }
      let row = null;
      let failed = false;
      try {
        row = await Fitment.getRow(vehicle);
      } catch (error) {
        failed = true;
      }
      if (request !== this.requests) return;

      let variant = this.currentVariant();
      let result = { level: 'unknown', reason: 'noSpecs' };
      if (failed) result = { level: 'unknown', reason: 'loadError' };
      else if (variant) result = Fitment.evaluate(row, Fitment.wheelFromVariant(this.product, variant));

      /* Another size of the same wheel may fit this vehicle better than the one showing. */
      const better = failed ? null : this.betterVariant(row, variant, result);
      if (better && !this.autoPicked && !new URLSearchParams(window.location.search).has('variant')) {
        /* first visit with a saved vehicle: open on the size that fits it */
        this.autoPicked = true;
        if (this.selectVariant(better.variant)) return;
      }
      this.autoPicked = true;
      this.better = better ? better.variant : null;
      const alternative = this.querySelector('[data-fitment-alternative]');
      if (alternative) alternative.hidden = !better;

      this.prompt.hidden = true;
      this.panel.hidden = false;
      setText(this.vehicleName, Fitment.label(vehicle));
      this.verdict.className = `product-fitment__verdict product-fitment__verdict--${result.level}`;
      setText(this.verdict, Fitment.verdictLabel(result.level, result));
      setText(this.detail, Fitment.verdictDetail(result));
      this.detail.hidden = !this.detail.textContent;
      this.renderSpecs(row);
    }

    /* The best-fitting variant in stock, when it fits better than the current one. */
    betterVariant(row, current, currentResult) {
      let best = null;
      this.product.variants.forEach((candidate) => {
        if (candidate.available === false || !Array.isArray(candidate.options)) return;
        const result = Fitment.evaluate(row, Fitment.wheelFromVariant(this.product, candidate));
        if (!best || Fitment.rank(result.level) > Fitment.rank(best.result.level)) best = { variant: candidate, result };
      });
      if (!best || Fitment.rank(best.result.level) < 2) return null;
      if (current && Number(current.id) === Number(best.variant.id)) return null;
      return Fitment.rank(best.result.level) > Fitment.rank(currentResult.level) ? best : null;
    }

    /* Asks the product's variant picker to switch to a variant. */
    selectVariant(variant) {
      const section = document.querySelector(`[data-product-section="${this.dataset.sectionId}"]`);
      const picker = section && section.querySelector('variant-picker');
      if (!picker || typeof picker.setSelected !== 'function' || typeof picker.onChange !== 'function') return false;
      picker.setSelected(variant.options);
      picker.onChange();
      return true;
    }

    renderSpecs(row) {
      const key = row ? `${row.make}|${row.model}|${row.from}` : '';
      if (this.specsKey === key) return;
      this.specsKey = key;
      this.specs.replaceChildren();
      if (!row) {
        this.specs.hidden = true;
        return;
      }
      const labels = this.specs.dataset;
      const rows = [
        [labels.labelBoltPattern, (row.pcd || []).join(', ')],
        [labels.labelBore, row.bore ? `${row.bore} mm` : ''],
        [labels.labelLug, row.lug],
        [labels.labelTorque, row.torque],
        [labels.labelFactory, row.oemSizes || (row.setups ? row.setups.join(', ') : '')],
      ].filter((item) => item[1]);
      rows.forEach(([label, value]) => {
        const group = element('div', 'product-fitment__spec');
        group.append(element('dt', null, label), element('dd', null, value));
        this.specs.append(group);
      });
      this.specs.hidden = rows.length === 0;
    }
  }

  /* vehicle-results-bar: saved vehicle and the native bolt pattern filter */

  class VehicleResultsBar extends HTMLElement {
    connectedCallback() {
      const filter = readJSON(this) || {};
      this.values = Array.isArray(filter.values) ? filter.values : [];
      this.empty = this.querySelector('[data-results-empty]');
      this.active = this.querySelector('[data-results-active]');
      this.nameNode = this.querySelector('[data-results-vehicle]');
      this.note = this.querySelector('[data-results-note]');
      this.apply = this.querySelector('[data-results-apply]');
      if (!this.empty || !this.active || !this.nameNode || !this.note || !this.apply) return;

      if (!this.bound) {
        this.bound = true;
        this.requests = 0;
        const clear = this.querySelector('[data-results-clear]');
        if (clear) clear.addEventListener('click', () => Fitment.setVehicle(null));
      }

      if (this.stopListening) this.stopListening();
      /* a vehicle picked on this page may apply the filter; one synced from another tab may not */
      this.stopListening = Theme.on('vehicle:changed', (event) => {
        if (!event.detail.vehicle && !event.detail.synced) this.removeFilter();
        this.render(!event.detail.synced);
      });
      this.render(this.arrivedFromPicker());
    }

    /*
      Moves wheels that fit the saved vehicle to the front of the grid on this
      page: direct fits first, then fits with modifications, then the rest. The
      shopper's chosen sort order is kept inside each group.
    */
    sortByFit(row) {
      const scope = this.closest('[data-facets-results]') || document;
      const grid = scope.querySelector('.product-grid');
      if (!grid) return;
      const items = Array.from(grid.children).map((item, index) => {
        const badge = item.querySelector('fitment-badge');
        const product = badge && (badge.product || readJSON(badge));
        const rank = product ? Fitment.rank(Fitment.evaluateProduct(row, product).level) : 1;
        return { item, index, rank };
      });
      const sorted = items.slice().sort((first, second) => second.rank - first.rank || first.index - second.index);
      if (sorted.every((entry, index) => entry === items[index])) return;
      sorted.forEach((entry) => grid.append(entry.item));
    }

    /* Updates the listing in place when the filters can do it, otherwise loads the URL. */
    go(url) {
      const facets = document.querySelector('facets-form');
      if (facets && typeof facets.renderUrl === 'function') facets.renderUrl(url);
      else window.location.assign(url);
    }

    /* The vehicle was cleared: take its bolt pattern filter off the listing too. */
    removeFilter() {
      const active = this.values.filter((value) => value.active && value.param);
      if (!active.length) return;
      const url = new URL(window.location.href);
      active.forEach((stale) => {
        const kept = url.searchParams.getAll(stale.param).filter((item) => item !== String(stale.value));
        url.searchParams.delete(stale.param);
        kept.forEach((item) => url.searchParams.append(stale.param, item));
      });
      url.searchParams.delete('page');
      this.go(`${url.pathname}${url.search}`);
    }

    /* True once per page load when the shopper has just come from a vehicle picker. */
    arrivedFromPicker() {
      const url = new URL(window.location.href);
      if (url.searchParams.get('fit') !== '1' || arrivalHandled) return false;
      arrivalHandled = true;
      url.searchParams.delete('fit');
      window.history.replaceState(window.history.state, '', url.toString());
      return true;
    }

    disconnectedCallback() {
      if (this.stopListening) this.stopListening();
      this.stopListening = null;
    }

    /*
      The listing URL with every matching bolt pattern selected and patterns
      left over from another vehicle removed. Built from the URL Shopify gave
      for the filter value; null unless it stays on this storefront.
    */
    filterUrl(matches) {
      let url;
      try {
        url = new URL(matches[0].url, window.location.origin);
      } catch (error) {
        return null;
      }
      if (url.origin !== window.location.origin) return null;

      const params = url.searchParams;
      params.delete('fit');
      matches.slice(1).forEach((value) => {
        if (value.param && !params.getAll(value.param).includes(String(value.value))) params.append(value.param, value.value);
      });
      this.values
        .filter((value) => value.active && value.param && !matches.includes(value))
        .forEach((stale) => {
          const kept = params.getAll(stale.param).filter((item) => item !== String(stale.value));
          params.delete(stale.param);
          kept.forEach((item) => params.append(stale.param, item));
        });
      return `${url.pathname}${url.search}`;
    }

    async render(mayRedirect) {
      const request = (this.requests += 1);
      const vehicle = Fitment.getVehicle();
      this.empty.hidden = Boolean(vehicle);
      this.active.hidden = !vehicle;
      if (!vehicle) return;

      setText(this.nameNode, Fitment.label(vehicle));
      this.apply.hidden = true;

      let row;
      try {
        row = await Fitment.getRow(vehicle);
      } catch (error) {
        if (request === this.requests) setText(this.note, strings.loadError);
        return;
      }
      if (request !== this.requests || !this.isConnected) return;
      if (!row) {
        setText(this.note, strings.noData);
        return;
      }

      this.sortByFit(row);

      const labels = this.dataset;
      const patterns = row.pcd.join(', ');
      const patternNote = `${labels.labelBoltPattern}: ${patterns}`;
      const matches = this.values.filter((value) =>
        Fitment.parseBoltPatterns(value.label, true).some((pcd) => row.pcd.includes(pcd))
      );
      const stocked = matches.filter((value) => value.count !== 0);
      const url = stocked.length ? this.filterUrl(stocked) : null;

      if (!this.values.length) {
        setText(this.note, patternNote);
      } else if (!stocked.length) {
        setText(this.note, (labels.labelNoMatches || '').replace('[pattern]', patterns));
      } else if (matches.some((value) => value.active)) {
        setText(this.note, (labels.labelFiltered || '').replace('[pattern]', patterns));
      } else if (!url) {
        setText(this.note, patternNote);
      } else if (mayRedirect && config.autoFilter && !leaving) {
        this.go(url);
      } else {
        setText(this.note, patternNote);
        this.apply.href = url;
        this.apply.hidden = false;
      }
    }
  }

  /* vehicle-finder: the homepage search, with coverage count and saved vehicle */

  class VehicleFinder extends HTMLElement {
    connectedCallback() {
      this.saved = this.querySelector('[data-finder-saved]');
      this.picker = this.querySelector('[data-finder-picker]');
      this.nameNode = this.querySelector('[data-finder-vehicle]');
      this.shop = this.querySelector('[data-finder-shop]');
      this.coverage = this.querySelector('[data-finder-coverage]');
      if (!this.saved || !this.picker || !this.nameNode) return;

      if (!this.bound) {
        this.bound = true;
        if (this.shop && config.collectionUrl) {
          const url = new URL(config.collectionUrl, window.location.origin);
          url.searchParams.set('fit', '1');
          this.shop.href = url.toString();
        }
        const change = this.querySelector('[data-finder-change]');
        if (change) {
          change.addEventListener('click', () => {
            this.show(null);
            focusPicker(this.picker);
          });
        }
      }

      if (this.stopListening) this.stopListening();
      this.stopListening = Theme.on('vehicle:changed', (event) => this.show(event.detail.vehicle));
      this.show(Fitment.getVehicle());
      if (this.coverage && this.coverage.hidden) whenVisible(this, () => this.renderCoverage());
    }

    disconnectedCallback() {
      if (this.stopListening) this.stopListening();
      this.stopListening = null;
    }

    show(vehicle) {
      if (vehicle && leaving) return;
      this.saved.hidden = !vehicle;
      this.picker.hidden = Boolean(vehicle);
      if (vehicle) this.nameNode.textContent = Fitment.label(vehicle);
    }

    async renderCoverage() {
      let rows;
      try {
        rows = await Fitment.loadIndex();
      } catch (error) {
        return;
      }
      if (!rows.length) return;
      const years = Fitment.years(rows);
      const values = {
        vehicles: Number(Fitment.index.combinations || 0).toLocaleString(),
        makes: Object.keys(Fitment.index.makes).length,
        from: years[years.length - 1],
        to: years[0],
      };
      this.coverage.textContent = this.coverage.dataset.template.replace(/\[(\w+)\]/g, (match, key) => values[key]);
      this.coverage.hidden = false;
    }
  }

  /* fitment-guide-dialog: the wheel and tire size guide, opened from the header menu */

  class FitmentGuideDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.body = this.querySelector('[data-fitment-guide-body]');
      this.status = this.querySelector('[data-fitment-guide-status]');
      if (!this.dialog || !this.body || this.onOpen) return;

      this.onOpen = (event) => {
        const trigger = event.target.closest('[data-fitment-guide-open]');
        if (!trigger) return;
        event.preventDefault();
        this.open();
      };
      document.addEventListener('click', this.onOpen);
      this.querySelectorAll('[data-fitment-guide-close]').forEach((button) =>
        button.addEventListener('click', () => this.dialog.close())
      );
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
    }

    disconnectedCallback() {
      if (this.onOpen) document.removeEventListener('click', this.onOpen);
      this.onOpen = null;
    }

    open() {
      if (typeof this.dialog.showModal !== 'function') return;
      /* Opened from the mobile menu: close that drawer so it is not left open underneath. */
      const header = document.querySelector('site-header');
      if (header && header.activeOverlay && typeof header.closeOverlay === 'function') header.closeOverlay(false);
      if (!this.dialog.open) this.dialog.showModal();
      if (!this.loaded && !this.loading) this.load();
    }

    async load() {
      this.loading = true;
      try {
        const html = await Theme.fetchSection(this.dataset.url, this.dataset.section);
        const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (!section) throw new Error('Guide section missing from response');
        section.querySelectorAll('script[src]').forEach((script) => script.remove());
        this.body.replaceChildren(...Array.from(section.childNodes));
        if (!document.querySelector(`script[src="${this.dataset.script}"]`)) {
          const script = document.createElement('script');
          script.src = this.dataset.script;
          script.defer = true;
          document.head.append(script);
        }
        this.loaded = true;
      } catch (error) {
        if (this.status) this.status.textContent = this.status.dataset.error;
      } finally {
        this.loading = false;
        this.body.removeAttribute('aria-busy');
      }
    }
  }

  /* Any control marked data-vehicle-clear removes the saved vehicle. */
  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-vehicle-clear]');
    if (!control) return;
    event.preventDefault();
    Fitment.setVehicle(null);
  });
  document.documentElement.classList.toggle('has-vehicle', Boolean(Fitment.getVehicle()));

  const elements = {
    'vehicle-picker': VehiclePicker,
    'vehicle-menu': VehicleMenu,
    'vehicle-strip': VehicleStrip,
    'fitment-badge': FitmentBadge,
    'product-fitment': ProductFitment,
    'vehicle-results-bar': VehicleResultsBar,
    'vehicle-finder': VehicleFinder,
    'fitment-guide-dialog': FitmentGuideDialog,
  };

  Object.keys(elements).forEach((name) => {
    if (!customElements.get(name)) customElements.define(name, elements[name]);
  });
})();
